import { SEPERATOR } from "../entities/Constants";
import { FormSectionElements, FormSections, ProgramContainer } from "../entities/formTemplateDataType";
import { getRootComponent } from "./ElementUtils";
import { deepClone, shallowClone } from "./JsonUtils";

export default class SectionUtils {

    static hydrateParentReferences(programContainer: ProgramContainer): void {
        if (!programContainer?.ListOfFormSections) return;
        for (const section of programContainer.ListOfFormSections) {
            section.parent = programContainer;
            const subSections = section.ListOfFormSubSections || [];
            for (const subSection of subSections) {
                subSection.parent = section;
                const elements = subSection.ListOfFormSectionElements || [];
                for (const element of elements) {
                    element.parent = subSection;
                    if (element.Datatype === "Row" && Array.isArray(element.subSectionList)) {
                        for (const rowSub of element.subSectionList) {
                            if (!rowSub) continue;
                            rowSub.parent = element;
                            const rowChildren = rowSub.ListOfFormSectionElements || [];
                            element.children = rowChildren;
                            for (const rowElement of rowChildren) {
                                rowElement.parent = rowSub;
                            }
                        }
                    }
                }
            }
        }
    }

    static getElementByFieldName(fieldName: string, formSection: FormSections) {
        const root = formSection.parent;
        const formList = root.ListOfFormSections;
        formList.forEach(section => {
            section.forEach(sub => {
                const elements = sub.ListOfFormSectionElements;
                elements.forEach(ele => {
                    if (ele.LoadBindName == fieldName)
                        return ele;
                })
            })
        });

        return null;
    };

    static getFieldsAboveTitle(formSection: FormSections) {
        let list = [];
        const subformList = formSection?.ListOfFormSubSections;
        if (subformList) {
            subformList.forEach(sub => {
                const fields = sub.ListOfFormSectionElements.filter(ele => ele.FieldMustBeAtTop);
                if (fields.length > 0) {
                    list.push(...fields);
                }
            });
        }


        return list;
    };

    static findNotCountedStep(programContainer: ProgramContainer): number {
        const sortedList = programContainer.ListOfFormSections;
        let notCountedPosition;
        const notCountedSections = sortedList.filter(p => p.NotCountedInSteps && p.Visible);
        let notCountedSection
        if (notCountedSections.length > 0) {
            notCountedSection = notCountedSections[0];
            notCountedPosition = sortedList.findIndex(obj => obj.Id == notCountedSection.Id);
            return notCountedPosition;
        }

        return -1;
    }

    static HasCustimzedSummary(programContainer: ProgramContainer) {
        const formList = programContainer.ListOfFormSections;
        const forms = formList.filter(p => p.CustomizedReviewPage && !p.ExcludeFromCustomizedSummary);
        const customized = forms.length > 0
        return customized;
    };

    static GetTechErrorSectionIndex(programContainer: ProgramContainer): number {
        const formList = programContainer.ListOfFormSections;
        const forms = formList.filter(p => p.IsTechErrorPage);
        const techErrorSection = forms[0];
        const techErrorSectionIndex = formList.findIndex(p => p === techErrorSection);
        return techErrorSectionIndex;
    }

    static turnOffSectionReadonly(section: FormSections) {
        section.SectionReadonly = false;
        const subSections = section.ListOfFormSubSections;
        for (const subSection of subSections) {
            const elements = subSection.ListOfFormSectionElements;
            for (const element of elements) {
                element.ReadOnly = false;
            }
        }
    }

    /**
     * Apply IsDirty flags on each element based on initial vs current values.
     * The value maps are expected to be keyed by LoadBindName.
     */
    static applyDirtyFlags(
        programContainer: ProgramContainer,
        initialValues: { [key: string]: any } | null,
        currentValues: { [key: string]: any } | null
    ): void {
            const excludedTypes = [
      'label',
      'html',
      'button',
      'submit',
      'reset',
      'navigation',
      'stepper',
      'link'
    ];
    
        if (!programContainer?.ListOfFormSections) {
            return;
        }

        const sections = programContainer.ListOfFormSections;

        for (const section of sections) {
            const subSections = section.ListOfFormSubSections || [];
            for (const subSection of subSections) {
                const elements = subSection.ListOfFormSectionElements || [];
                for (const element of elements) {
                    if (!element || !element.LoadBindName) {
                        continue;
                    }
                if (element && excludedTypes.includes(element.Datatype.toLowerCase())) {
                    continue;
                    }
                    const key = element.LoadBindName;
                    const initial = initialValues ? initialValues[key] : undefined;
                    const current = currentValues ? currentValues[key] : undefined;

                    const initialSerialized = initial !== undefined ? JSON.stringify(initial) : null;
                    const currentSerialized = current !== undefined ? JSON.stringify(current) : null;

                    element.IsDirty = initialSerialized !== currentSerialized;
                }
            }
        }
    }

    /**
     * Reset all IsDirty flags on the form back to false.
     */
    static resetDirtyFlags(programContainer: ProgramContainer): void {
        if (!programContainer?.ListOfFormSections) {
            return;
        }

        for (const section of programContainer.ListOfFormSections) {
            const subSections = section.ListOfFormSubSections || [];
            for (const subSection of subSections) {
                const elements = subSection.ListOfFormSectionElements || [];
                for (const element of elements) {
                    if (!element) {
                        continue;
                    }
                    element.IsDirty = false;

                    if (element.Datatype === 'Row' && Array.isArray(element.subSectionList)) {
                        element.subSectionList.forEach(rowSub => {
                            (rowSub?.ListOfFormSectionElements || []).forEach(rowElement => {
                                if (rowElement) {
                                    rowElement.IsDirty = false;
                                }
                            });
                        });
                    }
                }
            }
        }
    }

    static isSectionDirty(section: FormSections): boolean {
        const subSections = section.ListOfFormSubSections || [];
        for (const subSection of subSections) {
            const elements = subSection.ListOfFormSectionElements || [];
            for (const element of elements) {
                if (element.IsDirty) {
                    return true;
                }
            }
        }
        return false;
    }

    static isFormDirty(programContainer: ProgramContainer): boolean {
        const formSections = programContainer.ListOfFormSections || [];
        for (const section of formSections) {
            if (this.isSectionDirty(section)) {
                return true;
            }
        }
        return false;
    }

    static initProgramContainer = (programContainer:ProgramContainer) => {
              const duplicatableGroupMap: Record<string, FormSections[]> =
                programContainer.ListOfFormSections.reduce((acc, section) => {
                  const groupID = section.duplicateGroupID?.trim();
                  if (!groupID) {
                    section.orginalClone = shallowClone(section);
                    return acc; // skip sections without groupID
                  }
                  if (!acc[groupID]) {
                    acc[groupID] = [];
                  }
                  acc[groupID].push(deepClone(section));
    
                  return acc;
              }, {} as Record<string, FormSections[]>);
              if(Object.keys(duplicatableGroupMap).length >0){
                programContainer.IsDirty = true;
                programContainer.duplicatableGroups = duplicatableGroupMap;
              }
    }

    static duplicateSection(programContainer: ProgramContainer, dupGroupId: string, current: FormSections, groupIndex?: number) {
       
       let nextGroupIndex = 0;
       const distinctGroupIndexes = new Set<number>();
       if(groupIndex!==undefined&& groupIndex!==null){ 
            programContainer.ListOfFormSections.forEach(section => {
                if (section.duplicateGroupID === dupGroupId) {
                    const index = section.duplicateGroupIndex ?? 0;
                    distinctGroupIndexes.add(index);
                }
            });
            nextGroupIndex = groupIndex;
       }else{
            let maxGroupIndex = 0;
            programContainer.ListOfFormSections.forEach(section => {
            if (section.duplicateGroupID === dupGroupId) {
                const index = section.duplicateGroupIndex ?? 0;
                // track max
                if (index > maxGroupIndex) {
                    maxGroupIndex = index;
                }
                // track distinct values
                distinctGroupIndexes.add(index);
            }
            });
            nextGroupIndex = maxGroupIndex + 1;
        }
        const distinctCount = distinctGroupIndexes.size;
        const sectionsForDup: FormSections[] = deepClone(programContainer.duplicatableGroups![dupGroupId]);
        console.log('sectionsForDup:', sectionsForDup);
        sectionsForDup.forEach(section => {
          section.parent = programContainer;
          section.duplicateGroupIndex = nextGroupIndex;
          const groupIndex = (distinctCount + 1).toString();
          section.stepTitle = section.stepTitle?.replace('#seq#', groupIndex);
          section.stepTitleFRA = section.stepTitleFRA?.replace('#seq#', groupIndex);
          section.ENUTitle = section.ENUTitle?.replace('#seq#', groupIndex);
          section.FRATitle = section.FRATitle?.replace('#seq#', groupIndex);
          section.ENUDescription = section.ENUDescription?.replace('#seq#', groupIndex);
          section.FRADescription = section.FRADescription?.replace('#seq#', groupIndex);

          section.ListOfFormSubSections?.forEach((sub, subIndex) => {
            sub.parent = section;
            if (sub.FilterField) {
                sub.FilterField = sub.FilterField.map(value => `${value}${SEPERATOR}${section.duplicateGroupIndex}`);
            }
            sub.ListOfFormSectionElements?.forEach((el, elIndex) => {
              el.parent = sub;
              if (el.FilterField) {
                el.FilterField =  `${el.FilterField}${SEPERATOR}${section.duplicateGroupIndex}`;
              }
              if (Array.isArray(el.ListOfLOVValues)) {
                el.ListOfLOVValues.forEach((lov, lovIndex) => {
                  if (lov.FilterField) {
                    lov.FilterField =  `${lov.FilterField}${SEPERATOR}${section.duplicateGroupIndex}`;
                  }
                  lov.parent =el;
                });
              }
              el.LoadBindName = (el.LoadBindName??'')+SEPERATOR+(nextGroupIndex).toString();
            });
          });
        });
        //const current = getParentSection(field);
        const currentIndex = current?programContainer.ListOfFormSections.findIndex( s => s === current || s.Id === current.Id):0;
        programContainer.ListOfFormSections.splice(currentIndex + 1, 0, ...sectionsForDup);
        for (let i = currentIndex; i < programContainer.ListOfFormSections.length; i++) {
          const section = programContainer.ListOfFormSections[i];
          section.Sequence = i + 1; // Sequence starts from 1
          section.Id = (i + 1).toString(); // Assuming Id is string
          section.duplicateGroupID?.trim();
          SectionUtils.replaceStep(section, section.orginalClone??section);
         //}
        }
    }

    static findSectionByFieldName(programContainer: ProgramContainer, fieldName: string): FormSections | null {
            const result = fieldName.includes(SEPERATOR) ? fieldName.split(SEPERATOR)[0] : null;
            const matchedSection = programContainer.ListOfFormSections.find(section =>
                section.ListOfFormSubSections?.some(sub =>
                    sub.ListOfFormSectionElements?.some(el => (el.LoadBindName??'').includes(SEPERATOR)&&(el.LoadBindName??'').split(SEPERATOR)[0] === result
                                                              || el.LoadBindName === result)
                )
            );

            return matchedSection


    }

    static replaceStep(section: FormSections, original: FormSections){
        
        if(!original) return;
        const base = section.Sequence - 1;
        const replacer = (_: string, op?: string, num?: string) => {
            if (!op || !num) {
            // #stp#
            return base.toString();
            }

            const offset = Number(num);
            return op === '+'
            ? (base + offset).toString()
            : (base - offset).toString();
        };

        const pattern = /#stp(?:([+-])(\d+))?#/g;
        // If section has preservePreScriptTitle flag, only replace if #stp# pattern exists
        if (section.preservePreScriptTitle) {
            if (original.ENUTitle?.match(pattern)) {
                section.ENUTitle = original.ENUTitle.replace(pattern, replacer);
            }
            if (original.FRATitle?.match(pattern)) {
                section.FRATitle = original.FRATitle.replace(pattern, replacer);
            }
        } else {
            section.ENUTitle = original.ENUTitle?.replace(pattern, replacer);
            section.FRATitle = original.FRATitle?.replace(pattern, replacer);
        }

    }

    /**
     * Reset the value of all the fields, except itself and provided exceptFields, in the ProgramContainer that the provided field associated with.
     */
    static resetAllFieldValues(
        field: FormSectionElements,
        exceptFields: string[] = []
    ): void {
        const excludedTypes = [
            'label',
            'html',
            'button',
            'submit',
            'reset',
            'navigation',
            'stepper',
            'link'
        ];
    
        const programContainer = getRootComponent(field);
        if (!programContainer?.ListOfFormSections) {
            return;
        }

        const sections = programContainer.ListOfFormSections;

        if (!exceptFields.includes(field.LoadBindName)) {
            exceptFields.push(field.LoadBindName);
        }
        for (const section of sections) {
            const subSections = section.ListOfFormSubSections || [];
            for (const subSection of subSections) {
                const elements = subSection.ListOfFormSectionElements || [];
                for (const element of elements) {
                    if (!element || !element.LoadBindName ||  exceptFields.includes(element.LoadBindName)) {
                        continue;
                    }
                    if (element && excludedTypes.includes(element.Datatype.toLowerCase())) {
                        continue;
                    }
                    element.Value = null;
                }
            }
        }
    }
}
